import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import toast from 'react-hot-toast';
import emailjs from 'emailjs-com';
import { logger } from '../utils/logger';
import { sanitizeInput, sanitizeEmail, validateURL } from '../utils/sanitizer';
import { sanitizeError } from '../utils/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { 
  Smartphone, 
  Calendar, 
  Users, 
  Clock,
  Edit,
  MessageSquare,
    Copy,
  ExternalLink,
  ArrowLeft
} from 'lucide-react';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const AppDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { isExpanded } = useSidebar();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [hasApplied, setHasApplied] = useState(false);
  const [applications, setApplications] = useState([]);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    platform: '',
    test_url: ''
  });

  useEffect(() => {
    // Supabase'den gerçek verileri çek
    const fetchApp = async () => {
      try {
        if (!id) {
          setLoading(false);
          return;
        }

        const response = await apiFetch(`/api/apps?id=eq.${id}&select=*`);
        const appsData = await response.json();

        if (!response.ok || !appsData || appsData.length === 0) {
          throw new Error('App fetch failed');
        }

        // Creator bilgisini çek
        const appData = appsData[0];
        const creatorResponse = await apiFetch(`/api/profiles?id=eq.${appData.user_id}&select=id,first_name,last_name,email`);
        const creatorData = await creatorResponse.json();

        if (creatorData && creatorData.length > 0) {
          appData.creator = creatorData[0];
        }

        setApp(appData);
        
        // Edit form'unu güncelle
        setEditForm({
          title: appData.title || '',
          description: appData.description || '',
          platform: appData.platform || '',
          test_url: appData.test_url || ''
        });

        // Kullanıcının bu uygulamaya başvurup başvurmadığını kontrol et
        if (user && appData) {
          try {
            const applicationsResponse = await apiFetch(`/api/applications?select=id&app_id=eq.${appData.id}&user_id=eq.${user.id}`);
            const applications = await applicationsResponse.json();

            if (applications && applications.length > 0) {
              setHasApplied(true);
            }
          } catch (error) {
            logger.error('Başvuru kontrolü hatası:', error);
          }
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    const fetchApplications = async () => {
      try {
        if (!id || !user) return; // user kontrolü ekle

        // Önce uygulamanın sahibi olduğunu kontrol et
        const appCheckResponse = await apiFetch(`/api/apps?select=user_id&id=eq.${id}&user_id=eq.${user.id}`);
        const appCheck = await appCheckResponse.json();

        if (!appCheck || appCheck.length === 0) {
          // Uygulama sahibi değilse başvuruları gösterme
          setApplications([]);
          return;
        }

        const applicationsResponse = await apiFetch(`/api/applications-by-app?app_id=${id}`);
        const data = await applicationsResponse.json();

        if (!applicationsResponse.ok) {
          throw new Error('Applications fetch failed');
        }

        // Her başvuru için user email'ini çek
        if (data && data.length > 0) {
          const applicationsWithUsers = await Promise.all(
            data.map(async (application) => {
              const userResponse = await apiFetch(`/api/profiles?id=eq.${application.user_id}&select=first_name,last_name,email`);
              const userData = await userResponse.json();
              
              return {
                ...application,
                user: userData[0] || null
              };
            })
          );
          setApplications(applicationsWithUsers);
        } else {
          setApplications(data || []);
        }
      } catch (error) {
        logger.error('Başvurular çekilirken hata:', error);
      }
    };

    fetchApp();
    fetchApplications();
  }, [id, user]);

  const handleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleApply = () => {
    setShowEmailModal(true);
  };

  const handleAcceptApplication = async (applicationId) => {
    try {
      const response = await apiFetch(`/api/applications-admin?id=${applicationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'accepted' })
      });

      if (!response.ok) {
        throw new Error('Application update failed');
      }

      // Başvuru listesini güncelle
      const updatedApplications = applications.map(application => 
        application.id === applicationId ? { ...application, status: 'accepted' } : application
      );
      setApplications(updatedApplications);

      // Gerçek kabul edilen kullanıcı sayısını hesapla
      const acceptedCount = updatedApplications.filter(app => app.status === 'accepted').length;
      
      // Apps tablosundaki current_testers sayısını güncelle
      const appResponse = await apiFetch(`/api/apps?id=eq.${app.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ current_testers: acceptedCount })
      });

      if (!appResponse.ok) {
        console.error('App update error');
      }

      // App state'ini güncelle
      setApp({ ...app, current_testers: acceptedCount });
      
      toast.success('Başvuru kabul edildi!');
      
    } catch (error) {
      toast.error('Başvuru kabul edilirken hata oluştu!');
    }
  };

  const handleRejectApplication = async (applicationId) => {
    try {
      const response = await apiFetch(`/api/applications-admin?id=${applicationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'rejected' })
      });

      if (!response.ok) {
        throw new Error('Application update failed');
      }

      // Başvuru listesini güncelle
      const updatedApplications = applications.map(app => 
        app.id === applicationId ? { ...app, status: 'rejected' } : app
      );
      setApplications(updatedApplications);

      // Gerçek kabul edilen kullanıcı sayısını hesapla
      const acceptedCount = updatedApplications.filter(app => app.status === 'accepted').length;
      
      // Apps tablosundaki current_testers sayısını güncelle
      const appResponse = await apiFetch(`/api/apps?id=eq.${app.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ current_testers: acceptedCount })
      });

      if (!appResponse.ok) {
        console.error('App update error');
      }

      // App state'ini güncelle
      setApp({ ...app, current_testers: acceptedCount });
      
      toast.success('Başvuru reddedildi!');
      
    } catch (error) {
      toast.error('Başvuru reddedilirken hata oluştu!');
    }
  };

  const handleRemoveApplication = async (applicationId) => {
    try {
      const response = await apiFetch(`/api/applications-admin?id=${applicationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'removed' })
      });

      if (!response.ok) {
        throw new Error('Application update failed');
      }

      // Başvuru listesini güncelle
      const updatedApplications = applications.map(app => 
        app.id === applicationId ? { ...app, status: 'removed' } : app
      );
      setApplications(updatedApplications);

      // Gerçek kabul edilen kullanıcı sayısını hesapla
      const acceptedCount = updatedApplications.filter(app => app.status === 'accepted').length;
      
      // Apps tablosundaki current_testers sayısını güncelle
      const appResponse = await apiFetch(`/api/apps?id=eq.${app.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ current_testers: acceptedCount })
      });

      if (!appResponse.ok) {
        console.error('App update error');
      }

      // App state'ini güncelle
      setApp({ ...app, current_testers: acceptedCount });
      
      toast.success('Tester çıkarıldı!');
      
    } catch (error) {
      toast.error('Tester çıkarılırken hata oluştu!');
    }
  };

  const handleEmailSubmit = async () => {
    try {
      if (!app?.id || !email || !user?.id) {
        return;
      }

      // Basit rate limit (DB insert öncesi)
      const now = Date.now();
      const last = Number(localStorage.getItem('email_notify_last_ts') || 0);
      const diffMs = now - last;
      if (diffMs < 180_000) {
        const remain = Math.ceil((180_000 - diffMs) / 1000);
        toast.error(`Lütfen ${remain} sn sonra tekrar deneyin.`);
        return;
      }

      // Duplicate kontrolü
      const existingResponse = await apiFetch(`/api/applications?select=id&app_id=eq.${app.id}&user_id=eq.${user.id}`);
      const existingApplication = await existingResponse.json();

      if (existingApplication && existingApplication.length > 0) {
        toast.error('Bu uygulamaya zaten başvurmuşsunuz!');
        return;
      }

      // API'ye başvuru kaydet
      const response = await apiFetch('/api/applications', {
        method: 'POST',
        body: JSON.stringify({
          app_id: app.id,
          user_id: user.id,
          email: sanitizeEmail(email),
          status: 'pending'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Başvuru kaydedilemedi');
      }

      // Uygulama sahibine EmailJS ile e-posta bildirimi gönder (Türkçe)
      try {
        if (app?.creator?.email) {
          await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            {
              to_email: app.creator.email,
              subject: `Yeni Tester Başvurusu • ${app.title}`,
              from_name: '12Tester Bildirim',
              from_email: user.email,
              title: app.title,
              app_url: `https://12tester.com.tr/app/${app.id}`,
              message: `${user.email} kullanıcısı ${app.title} uygulamanıza tester olarak başvurdu.`
            },
            EMAILJS_PUBLIC_KEY
          );
          localStorage.setItem('email_notify_last_ts', String(now));
        }
      } catch (e) {
        logger.error('Başvuru maili gönderilemedi', e);
      }

      setShowEmailModal(false);
      setEmail('');
      setHasApplied(true);
      toast.success('Başvurunuz başarıyla gönderildi!');
    } catch (error) {
      logger.error('Başvuru gönderilirken hata:', error);
      toast.error(sanitizeError(error));
    }
  };

  const handleSave = async () => {
    try {
      if (!app?.id) {
        toast.error('Uygulama ID bulunamadı!');
        return;
      }

      // Input sanitization
      const sanitizedData = {
        title: sanitizeInput(editForm.title),
        description: sanitizeInput(editForm.description),
        platform: sanitizeInput(editForm.platform),
        test_url: editForm.test_url ? validateURL(editForm.test_url) : editForm.test_url
      };

      // API'ye uygulama güncellemesi gönder
      const response = await apiFetch(`/api/apps?id=eq.${app.id}`, {
        method: 'PATCH',
        body: JSON.stringify(sanitizedData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Uygulama güncellenemedi');
      }

      // App state'ini güncelle
      setApp({
        ...app,
        ...sanitizedData
      });

      setIsEditing(false);
      toast.success('Uygulama başarıyla güncellendi!');
    } catch (error) {
      logger.error('Uygulama güncellenirken hata:', error);
      toast.error(sanitizeError(error));
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-emerald-600"></div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Uygulama bulunamadı</h3>
          <p className="text-gray-500">Aradığınız uygulama mevcut değil.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header Gradient */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-48 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      <div className={`relative -mt-32 min-h-screen pb-16 transition-all duration-300 ${
        isExpanded ? 'md:ml-72' : 'md:ml-18'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
            <div className="backdrop-blur-xl bg-white/30 rounded-2xl p-6 shadow-xl border border-white/40 mb-6">
              <div className="flex items-center justify-between mb-4">
            <Link
              to="/my-apps"
                  className="inline-flex items-center text-sm text-white hover:text-emerald-100 transition-colors font-medium drop-shadow-md"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Uygulamalarıma Dön
            </Link>
                
                {/* Düzenle butonu yukarıda */}
                {user && app.user_id === user.id && (
                  <button
                    onClick={handleEdit}
                    className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 text-sm font-medium rounded-xl text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Düzenle
                  </button>
                )}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg border border-white/20">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-white drop-shadow-lg">{app.title}</h1>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/90 text-emerald-800 mt-2 shadow-md border border-white/50">
                {app.platform}
              </span>
            </div>
                </div>
                <div className="flex space-x-3">
                  {user && app.user_id !== user.id && (
                    hasApplied ? (
                      <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-sm font-medium rounded-xl text-white shadow-lg">
                        Başvuruldu
                      </span>
                    ) : (
            <button
                        onClick={handleApply}
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-sm font-medium rounded-xl text-white hover:shadow-xl transform hover:scale-105 transition-all duration-300 shadow-lg"
            >
                        Başvur
            </button>
                    )
                  )}
                </div>
          </div>
                             <div className="mt-6 pt-4 border-t border-white/20">
                 <div className="inline-block bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-md">
                   <p className="text-gray-900 text-sm font-medium">
                     Oluşturan: <span 
                       className="text-emerald-600 font-semibold hover:text-emerald-800 hover:underline cursor-pointer transition-colors"
                       onClick={() => window.location.href = `/profile/${app.creator?.id}`}
                     >
                       {app.creator?.first_name && app.creator?.last_name 
                         ? `${app.creator.first_name} ${app.creator.last_name}`
                         : app.creator?.first_name || app.creator?.email?.split('@')[0] || 'Bilinmiyor'
                       }
                     </span>
                   </p>
                 </div>
               </div>
            </div>
        </div>

        {/* App Details */}
          <div className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg mb-8 border border-white/40">
            <div className="px-8 py-6 border-b border-white/30">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                  <Smartphone className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Uygulama Detayları</h2>
              </div>
          </div>
          
            <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Uygulama Adı
                  </label>
                  <input
                    type="text"
                       value={isEditing ? editForm.title : app.title}
                       onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                    disabled={!isEditing}
                       className={`w-full px-4 py-3 border rounded-xl transition-all duration-300 ${
                      isEditing 
                           ? 'border-emerald-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white' 
                           : 'border-white/40 bg-white/40 text-gray-900'
                    }`}
                  />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Test URL
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                         value={isEditing ? editForm.test_url : app.test_url}
                         onChange={(e) => setEditForm({...editForm, test_url: e.target.value})}
                      disabled={!isEditing}
                         className={`w-full px-4 py-3 pr-12 border rounded-xl transition-all duration-300 ${
                        isEditing 
                             ? 'border-emerald-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white' 
                             : 'border-white/40 bg-white/40 text-gray-900'
                      }`}
                    />
                    <a
                        href={app.test_url}
                      target="_blank"
                      rel="noopener noreferrer"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-emerald-600 hover:text-emerald-800 transition-colors"
                    >
                        <ExternalLink className="h-5 w-5" />
                    </a>
                  </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Platform
                  </label>
                  <select
                       value={isEditing ? editForm.platform : app.platform}
                       onChange={(e) => setEditForm({...editForm, platform: e.target.value})}
                    disabled={!isEditing}
                       className={`w-full px-4 py-3 border rounded-xl transition-all duration-300 ${
                      isEditing 
                           ? 'border-emerald-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white' 
                           : 'border-white/40 bg-white/40 text-gray-900'
                    }`}
                  >
                    <option value="Android">Android</option>
                    <option value="iOS">iOS</option>
                    <option value="Web">Web</option>
                    <option value="Desktop">Desktop</option>
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Açıklama
                  </label>
                  <textarea
                       value={isEditing ? editForm.description : app.description}
                       onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    disabled={!isEditing}
                    rows={4}
                       className={`w-full px-4 py-3 border rounded-xl transition-all duration-300 ${
                      isEditing 
                           ? 'border-emerald-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white' 
                           : 'border-white/40 bg-white/40 text-gray-900'
                    }`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Oluşturulma Tarihi
                    </label>
                    <input
                      type="text"
                        value={new Date(app.created_at).toLocaleDateString('tr-TR')}
                      disabled
                        className="w-full px-4 py-3 border border-white/40 bg-white/40 text-gray-900 rounded-xl"
                    />
                  </div>

                  <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Son Güncelleme
                    </label>
                    <input
                      type="text"
                        value={new Date(app.updated_at || app.created_at).toLocaleDateString('tr-TR')}
                      disabled
                        className="w-full px-4 py-3 border border-white/40 bg-white/40 text-gray-900 rounded-xl"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
                <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-white/30">
                <button
                  onClick={handleCancel}
                    className="px-6 py-3 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-300"
                >
                  İptal
                </button>
                <button
                  onClick={handleSave}
                    className="px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:shadow-xl transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-300"
                >
                  Güncelle
                </button>
              </div>
            )}
          </div>
        </div>

          {/* Applied Users - Sadece uygulama sahibi görsün */}
          {user && app.user_id === user.id && (
          <div className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg border border-white/40">
            <div className="px-8 py-6 border-b border-white/30">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Başvuran Kullanıcılar</h2>
              </div>
          </div>
          
            <div className="p-8">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-white/30">
                  <thead className="bg-white/40">
                  <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      TESTER
                    </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      STATUS
                    </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      APPLIED DATE
                    </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      FEEDBACK
                    </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      İŞLEMLER
                    </th>
                  </tr>
                </thead>
                  <tbody className="bg-white/20 divide-y divide-white/30">
                    {applications.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-gray-600">
                          Henüz hiçbir kullanıcı başvurmadı
                        </td>
                      </tr>
                    ) : (
                      applications.map((application) => (
                        <tr key={application.id} className="hover:bg-white/30 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                                  <span className="text-white font-medium text-sm">
                                    {application.user?.email?.charAt(0).toUpperCase() || 'U'}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {application.user?.email?.split('@')[0] || 'Bilinmiyor'}
                                </div>
                                <div className="flex items-center space-x-2 text-sm text-gray-600">
                                  <span className="truncate max-w-[200px]">
                                    {application.email || application.user?.email || 'E-posta yok'}
                                  </span>
                                  {(application.email || application.user?.email) && (
                                    <button
                                      type="button"
                                      title="E-postayı kopyala"
                                      className="inline-flex items-center px-2 py-1 rounded-md border border-white/40 bg-white/60 hover:bg-white/80 text-emerald-700 hover:text-emerald-800 transition-colors"
                                      onClick={() => {
                                        const mail = application.email || application.user?.email;
                                        if (!mail) return;
                                        try {
                                          navigator.clipboard.writeText(mail);
                                          toast.success('E-posta kopyalandı');
                                        } catch (_) {
                                          // Clipboard API başarısız olursa fallback
                                          const tmp = document.createElement('input');
                                          tmp.value = mail;
                                          document.body.appendChild(tmp);
                                          tmp.select();
                                          document.execCommand('copy');
                                          document.body.removeChild(tmp);
                                          toast.success('E-posta kopyalandı');
                                        }
                                      }}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                              application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              application.status === 'accepted' ? 'bg-emerald-100 text-emerald-800' :
                              application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              application.status === 'removed' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {application.status === 'pending' ? 'Beklemede' :
                               application.status === 'accepted' ? 'Kabul Edildi' :
                               application.status === 'rejected' ? 'Reddedildi' :
                               application.status === 'removed' ? 'Çıkarıldı' :
                               application.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(application.created_at).toLocaleDateString('tr-TR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            -
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button 
                                className="inline-flex items-center px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                                onClick={() => {
                                  try {
                                    if (application?.user_id) {
                                      localStorage.setItem('selectedUserId', application.user_id);
                                    }
                                  } catch (_) {}
                                  window.location.href = '/messages';
                                }}
                              >
                                <MessageSquare className="h-4 w-4 mr-1" /> Mesajlaş
                              </button>
                              {application.status === 'pending' && (
                                <>
                                  <button 
                                    className="inline-flex items-center px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700 transition-colors"
                                    onClick={() => handleAcceptApplication(application.id)}
                                  >
                                    Kabul Et
                                  </button>
                                  <button 
                                    className="inline-flex items-center px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors"
                                    onClick={() => handleRejectApplication(application.id)}
                                  >
                                    Reddet
                                  </button>
                                </>
                              )}
                              {application.status === 'accepted' && (
                                <button 
                                  className="inline-flex items-center px-3 py-1.5 rounded-md bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                                  onClick={() => handleRemoveApplication(application.id)}
                                >
                                  Çıkar
                                </button>
                              )}
                            </div>
                    </td>
                  </tr>
                      ))
                    )}
                </tbody>
              </table>
            </div>
          </div>
          </div>
          )}

          {/* Email Modal */}
          {showEmailModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-6 border w-96 shadow-2xl rounded-2xl bg-white backdrop-blur-xl">
                <div className="mt-3">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    E-posta Adresinizi Girin
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Lütfen uygulama sahibinin Google Play Store'daki test kullanıcıları listesine ekleyeceği e-posta adresinizi girin. Bu adres, test sürümüne erişiminiz için kullanılacaktır.
                  </p>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      E-posta
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ornek@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowEmailModal(false)}
                      className="px-6 py-3 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-300"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleEmailSubmit}
                      disabled={!email}
                      className="px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:shadow-xl transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-300"
                    >
                      Başvur
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AppDetail;
